import React, { useEffect, useRef, useState } from "react";

/**
 * Chrono robuste & précis (rAF + accumulateur d'1s)
 * - Ne redémarre pas sur interactions/renders (boucle montée une seule fois)
 * - Barre de progression épaisse, collée en bas :
 *   - Verte (Hajime) / Rouge (Mate)
 *   - Ambrée indéterminée en Golden Score
 * - Reset sécurisé : double clic (armement puis confirmation)
 */
const MainChronoComponent = ({
  chrono,                // secondes restantes (mode normal)
  setChrono,
  isChronoRunning,
  setIsChronoRunning,
  playGongSound,
  score1,
  score2,
  initialChrono,         // durée totale (sec) pour la progression
  goldenScoreMode,
  setGoldenScoreMode,
  goldenChrono,          // secondes écoulées (GS)
  setGoldenChrono,
}) => {
  /** Refs "vivantes" pour ne pas recréer la boucle */
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const accRef = useRef(0);      // accumulateur du mode normal
  const accGsRef = useRef(0);    // accumulateur GS

  const runningRef = useRef(isChronoRunning);
  const goldenRef  = useRef(goldenScoreMode);
  const chronoRef  = useRef(chrono);
  const gsRef      = useRef(goldenChrono);
  const s1Ref      = useRef(score1);
  const s2Ref      = useRef(score2);

  // --- Reset sécurisé (double clic) ---
  const [resetArmed, setResetArmed] = useState(false);
  const resetTimerRef = useRef(null);
  const ARM_WINDOW_MS = 2500;

  const armReset = () => {
    setResetArmed(true);
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setResetArmed(false);
    }, ARM_WINDOW_MS);
  };

  const disarmReset = () => {
    setResetArmed(false);
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = null;
  };

  const handleResetClick = () => {
    if (!resetArmed) {
      armReset(); // 1er clic -> armement, pas de reset
      return;
    }
    // 2e clic dans la fenêtre -> reset effectif
    disarmReset();
    performReset();
  };

  // synchroniser les refs à chaque render
  useEffect(() => { runningRef.current = isChronoRunning; }, [isChronoRunning]);
  useEffect(() => { goldenRef.current  = goldenScoreMode; }, [goldenScoreMode]);
  useEffect(() => { chronoRef.current  = chrono;          }, [chrono]);
  useEffect(() => { gsRef.current      = goldenChrono;    }, [goldenChrono]);
  useEffect(() => { s1Ref.current      = score1;          }, [score1]);
  useEffect(() => { s2Ref.current      = score2;          }, [score2]);

  /** Boucle rAF, montée une seule fois */
  useEffect(() => {
    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000; // secondes
      lastTsRef.current = ts;

      if (!runningRef.current) {
        // en pause : on "re-sync" pour ne pas accumuler du temps
        accRef.current = 0;
        accGsRef.current = 0;
      } else if (!goldenRef.current) {
        // Mode normal : décrémente par secondes entières
        if (chronoRef.current > 0) {
          accRef.current += dt;
          while (accRef.current >= 1 && chronoRef.current > 0) {
            chronoRef.current = Math.max(0, chronoRef.current - 1);
            setChrono((prev) => Math.max(0, prev - 1));
            accRef.current -= 1;
          }
          if (chronoRef.current === 0) {
            // Fin du temps réglementaire
            setIsChronoRunning(false);
            playGongSound();
            const a = s1Ref.current || {};
            const b = s2Ref.current || {};
            const equal =
              (a.ippon || 0) === (b.ippon || 0) &&
              (a.wazari || 0) === (b.wazari || 0) &&
              (a.yuko || 0) === (b.yuko || 0);
            if (equal) {
              setGoldenScoreMode(true);
              setGoldenChrono(0);
              accGsRef.current = 0;
            }
          }
        }
      } else {
        // Golden Score : incrémente par secondes entières
        accGsRef.current += dt;
        while (accGsRef.current >= 1) {
          gsRef.current = gsRef.current + 1;
          setGoldenChrono((prev) => prev + 1);
          accGsRef.current -= 1;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      accRef.current = 0;
      accGsRef.current = 0;
      disarmReset();
    };
  }, [setChrono, setGoldenChrono, setIsChronoRunning, playGongSound, setGoldenScoreMode]);

  const toggleChrono = () => setIsChronoRunning((v) => !v);

  const performReset = () => {
    setIsChronoRunning(false);
    setChrono(initialChrono ?? 180);
    setGoldenChrono(0);
    setGoldenScoreMode(false);
    accRef.current = 0;
    accGsRef.current = 0;
  };

  const modifyChrono = (amount) => {
    if (!goldenRef.current) {
      setChrono((prev) => Math.max(0, prev + amount));
      // garder la cohérence de la ref
      chronoRef.current = Math.max(0, chronoRef.current + amount);
    } else {
      setGoldenChrono((prev) => Math.max(0, prev + amount));
      gsRef.current = Math.max(0, gsRef.current + amount);
    }
  };

  const timeLabel = (sec) =>
    `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;

  // Progression (mode normal)
  const total = Math.max(1, Number.isFinite(initialChrono) ? initialChrono : 180);
  const progressPct =
    !goldenScoreMode ? Math.min(100, Math.max(0, ((total - chrono) / total) * 100)) : null;

  const barColor = goldenScoreMode
    ? "bg-amber-400"
    : isChronoRunning
    ? "bg-emerald-500"
    : "bg-rose-500";

  return (
    <div className="relative w-full rounded-xl border border-white/10 bg-black text-white px-2 sm:px-3 md:px-4 pt-2 sm:pt-3 md:pt-4 pb-6 sm:pb-7 md:pb-8">
      {/* Ligne du haut (boutons + chrono + actions) */}
      <div className="flex items-center justify-between gap-2">
        {/* +/- */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm sm:text-base hover:bg-slate-600 active:scale-[0.98]"
            onClick={() => modifyChrono(+10)}
          >
            +10s
          </button>
          <button
            className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm sm:text-base hover:bg-slate-600 active:scale-[0.98]"
            onClick={() => modifyChrono(-10)}
          >
            -10s
          </button>
        </div>

        {/* Affichage chrono */}
        <div className="flex-1 flex items-center justify-center">
          <div
            className={`w-full max-w-xs sm:max-w-sm md:max-w-md text-center font-extrabold tracking-tight
            ${goldenScoreMode ? "text-amber-400" : "text-white"}
            text-5xl sm:text-6xl md:text-7xl`}
          >
            {goldenScoreMode ? `GS ${timeLabel(goldenChrono)}` : timeLabel(chrono)}
          </div>
        </div>

        {/* Actions (séparateur + marge pour éviter miss-click) */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggleChrono}
            className={`rounded-xl px-4 py-2 sm:px-5 sm:py-2.5 text-base sm:text-lg font-semibold shadow
              ${isChronoRunning ? "bg-rose-600 hover:bg-rose-500" : "bg-emerald-600 hover:bg-emerald-500"} text-white`}
          >
            {isChronoRunning ? "Mate" : "Hajime"}
          </button>

          {/* petit séparateur visuel + marge pour éloigner Reset */}
          <div className="mx-1 sm:mx-2 h-6 sm:h-7 w-[1px] bg-white/20" />

          <button
            onClick={handleResetClick}
            className={`rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 text-white text-sm sm:text-base
              transition-colors
              ${resetArmed ? "bg-red-700 hover:bg-red-600" : "bg-slate-700 hover:bg-slate-600"}`}
            title={resetArmed ? "Cliquez pour confirmer le reset" : "Reset (double clic sécurisé)"}
          >
            {resetArmed ? "Confirmer" : "Reset"}
          </button>
        </div>
      </div>

      {/* Progress bar ÉPAISSE collée en bas */}
      <div className="absolute left-0 right-0 bottom-0 h-3 sm:h-4 md:h-5 bg-slate-600 overflow-hidden rounded-b-xl">
        {!goldenScoreMode ? (
          <div
            className={`h-full ${barColor} transition-[width] duration-200 ease-out`}
            style={{ width: `${progressPct}%` }}
          />
        ) : (
          <div className="relative h-full w-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${barColor} animate-[indeterminate_1.4s_linear_infinite]`}
              style={{ width: "35%" }}
            />
          </div>
        )}
      </div>

      {/* keyframes pour la barre indéterminée */}
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
};

export default MainChronoComponent;
